import { Button, FontIcon, Text } from '@intigral/shared';
import styles from '@intigral/shared/styles/components/carousel.module.sass';
import { MovieCard } from '@intigral/ui';
import { FC, useState } from 'react';
import h from '../../../../helpers/common';
import f2 from '../../../../helpers/formatter2';
import { useConfigurationSections } from '../../../../hooks/useConfiguration';
import { __ } from '../../../../translations';
import { Trailer } from '../../../detail-overlay/components/elements';
import { useMuteControl } from '../../hooks/useMuteControl';
import { useVerticalBlockComputations } from '../../hooks/useVerticalBlockComputations';
import { useVerticalBlockHandlers } from '../../hooks/useVerticalBlockHandlers';
import { VerticalBlockProps } from '../../types/VerticalBlock.types';
import { getImages, getTrailerUrl, useImageConfig, getTitle } from '../../utils/VerticalBlock.utils';
import CatchUpDetailModal from '../../../../components/elements/catchup/CatchUpDetailModal';
import useLanguage from '../../../../hooks/useLanguage';

export const VerticalBlock: FC<VerticalBlockProps> = ({ item, promoHelpers }) => {
    // Local States
    const [isActive, setIsActive] = useState(false);
    const [isMuted, setIsMuted] = useState(true);

    // Custom Hooks & Utilities
    const language = useLanguage();
    const { autoTrailerCarousel } = useConfigurationSections();
    const IMAGE_CONFIG = useImageConfig();
    const { handleItemClick } = useVerticalBlockHandlers();
    const { handleMute } = useMuteControl(isMuted, setIsMuted);

    const { getProviderLogo, getPromoButtons } = promoHelpers;
    const buttonsObject = getPromoButtons(item).buttonsObject;

    const { play, reminder, watchlist, getIconName, getDetailsButtonProps, isLockedLinear } =
        useVerticalBlockComputations(item, buttonsObject);
    const { titleLogo, portrait, landscape } = getImages(item.images);

    // Derive trailer url
    const trailerURL = getTrailerUrl(item);

    const isLinear = ['REWIND', 'CHANNEL', 'CATCHUP', 'LIVESTREAM'].includes(item.promoType);

    return (
        <>
            <MovieCard
                layout="vertical-2x"
                heading={
                    <Text as="h2" variant="h2" size={{ default: '24', md: '28' }} alignment="start" margin="0">
                        {getTitle(item, language)}
                    </Text>
                }
                headingImage={titleLogo}
                onClick={() => handleItemClick(item)}
                icon={getIconName()}
                reminderButton={reminder}
                play={play}
                watchlistButton={watchlist}
                provider={[getProviderLogo(item)]}
                cover={
                    h.getImagizerUrl(
                        portrait,
                        IMAGE_CONFIG.portrait.width,
                        IMAGE_CONFIG.portrait.height,
                        IMAGE_CONFIG.portrait.quality
                    ) || null
                }
                rating={item?.ratings ? __(`rating_${item?.ratings.toLowerCase()}`) : null}
                info={[
                    ...(item?.genre && Array.isArray(item?.genre) ? item?.genre.map((g) => __(g)) || [] : []),
                    item?.duration > 0 ? `${f2.convertSecondsToMinutes(item?.duration)} ${__('min')}` : null,
                    isLinear && (item.title || item.channelTitle),
                    isLinear && item.channelNumber && `${__('promo_channel_code')} ${item.channelNumber}`,
                    item?.year ? `${item?.year}` : null,
                ]}
                page={getDetailsButtonProps(handleItemClick)}
                picture={
                    isActive && trailerURL && autoTrailerCarousel?.is_enabled && !isLockedLinear ? (
                        <Trailer source={trailerURL} isMuted={isMuted} item={item}>
                            <Button
                                data-test-id="mute-button"
                                className={styles['button-mute']}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMute();
                                }}
                            >
                                <FontIcon name={isMuted ? 'mute' : 'volume-1'} size="smaller" md="smaller" />
                            </Button>
                        </Trailer>
                    ) : (
                        <img
                            src={
                                h.getImagizerUrl(
                                    landscape,
                                    IMAGE_CONFIG.landscape.width,
                                    IMAGE_CONFIG.landscape.height
                                ) || null
                            }
                            alt=""
                        />
                    )
                }
                setIsActive={setIsActive}
                guid={item?.guid}
                {...(item.promoType === 'LIVESTREAM' ? { live: __('streaming_carousel_live_badge_text') } : {})}
            />
        </>
    );
};

export default VerticalBlock;
